import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { setMobilePref } from './useViewport';

interface NavItem { to: string; label: string; match: (p: string) => boolean; icon: React.ReactNode; }

const Icon: React.FC<{ d: string; className?: string }> = ({ d, className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className || 'w-6 h-6'}><path d={d} /></svg>
);

const NAV: NavItem[] = [
  {
    to: '/m/customers',
    label: 'Customers',
    match: (p) => p.startsWith('/m/customers') || p === '/m' || p === '/m/',
    icon: <Icon d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.2c-3.3 0-9.8 1.6-9.8 4.9v2.7h19.6v-2.7c0-3.3-6.5-4.9-9.8-4.9z" />,
  },
  {
    to: '/m/reports',
    label: 'Reports',
    match: (p) => p.startsWith('/m/reports'),
    icon: <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />,
  },
  {
    to: '/m/profile',
    label: 'Profile',
    match: (p) => p.startsWith('/m/profile'),
    icon: <Icon d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.2c-3.3 0-9.8 1.6-9.8 4.9v2.7h19.6v-2.7c0-3.3-6.5-4.9-9.8-4.9z" />,
  },
];

export const MobileTopBar: React.FC<{ title: string; back?: boolean; right?: React.ReactNode }> = ({ title, back, right }) => {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-3 py-3 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {back && (
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(-1)}
            className="-ml-1 flex h-9 w-9 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <Icon d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4L10.8 12z" className="w-5 h-5" />
          </button>
        )}
        <h1 className="truncate text-base font-semibold text-slate-900 dark:text-white">{title}</h1>
      </div>
      {right}
    </header>
  );
};

const MobileLayout: React.FC<{ children: React.ReactNode; title?: string; back?: boolean; topRight?: React.ReactNode; hideNav?: boolean }>
  = ({ children, title, back, topRight, hideNav }) => {
  const location = useLocation();
  return (
    <div className="flex h-full min-h-screen w-full flex-col bg-slate-50 dark:bg-slate-950">
      {title !== undefined && <MobileTopBar title={title} back={back} right={topRight} />}
      <main className="flex-1 overflow-y-auto pb-20">{children}</main>
      {!hideNav && (
        <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-3 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] dark:border-slate-700 dark:bg-slate-900">
          {NAV.map((item) => {
            const active = item.match(location.pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex flex-col items-center justify-center gap-1 py-2 text-xs font-medium transition-colors ${
                  active
                    ? 'text-primary'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
                style={active ? { color: '#1993e5' } : undefined}
              >
                <span className={`flex h-6 w-6 items-center justify-center`} style={active ? { color: '#1993e5' } : undefined}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
};

export const SwitchToDesktopLink: React.FC = () => (
  <button
    type="button"
    onClick={() => { setMobilePref('desktop'); window.location.href = '/'; }}
    className="text-xs font-medium text-primary hover:underline"
    style={{ color: '#1993e5' }}
  >
    Desktop site
  </button>
);

export default MobileLayout;
